import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export const RESTAURANT_TABLES_QUERY_ROOT = "restaurant-tables-exist";

export const restaurantTablesQueryKey = (workspaceId: string | null) => [RESTAURANT_TABLES_QUERY_ROOT, workspaceId] as const;

export async function fetchHasRestaurantTables(workspaceId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from("restaurant_tables")
    .select("id", { count: "exact", head: true })
    .eq("organization_id", workspaceId)
    .is("deleted_at", null);
  if (error) {
    console.warn("useHasRestaurantTables:", error.message);
    return false;
  }
  return (count ?? 0) > 0;
}

export function useHasRestaurantTables(): boolean {
  return useRestaurantTablesStatus().hasTables;
}

export function useRestaurantTablesStatus(enabled = true): { hasTables: boolean; loading: boolean } {
  const { activeWorkspaceId } = useAuth();
  const workspaceId = activeWorkspaceId ?? null;
  const active = enabled && !!workspaceId;
  const { data, isPending } = useQuery({
    queryKey: restaurantTablesQueryKey(workspaceId),
    queryFn: () => fetchHasRestaurantTables(workspaceId as string),
    enabled: active,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
  return { hasTables: active ? data === true : false, loading: active && isPending };
}

export function useRefreshRestaurantTables(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [RESTAURANT_TABLES_QUERY_ROOT] }),
    [queryClient],
  );
}
